<!-- staff/StaffLeaves.vue -->
<template>
  <section class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
    <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
      Leave History
    </h2>
    
    <div v-if="loading" class="text-sm text-gray-500">Loading...</div>
    <p v-else-if="!leaves.length" class="text-sm text-gray-500">No leave records found</p>
    
    <!-- Table of leaves -->
    <div v-else class="overflow-x-auto -mx-6">
      <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead class="bg-gray-50 dark:bg-gray-700/50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Leave Type</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Dates</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Reason</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Current Step</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Applied On</th>
          </tr>
        </thead>
        <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          <tr v-for="leave in leaves" :key="leave.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
            <!-- Leave Type -->
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
              {{ leave.leave_type?.name || leave.leave_type_name || `Type #${leave.leave_type_id}` }}
            </td>
            
            <!-- Dates -->
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
              <div>{{ formatDate(leave.start_date) }}</div>
              <div class="text-xs text-gray-500 dark:text-gray-400">to {{ formatDate(leave.end_date) }}</div>
            </td>
            
            <!-- Reason -->
            <td class="px-6 py-4 text-sm text-gray-900 dark:text-gray-200 max-w-xs">
              <div class="truncate" :title="leave.reason">{{ leave.reason || '-' }}</div>
            </td>
            
            <!-- Status Badge -->
            <td class="px-6 py-4 whitespace-nowrap text-sm">
              <span :class="statusClass(leave.status)" class="px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full capitalize">
                {{ leave.status }}
              </span>
            </td>
            
            <!-- Current Step -->
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200 capitalize">
              {{ leave.current_step || 'N/A' }}
            </td>
            
            <!-- Applied Date -->
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
              {{ formatDate(leave.created_at) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script setup>
import { ref, inject, onMounted } from 'vue'
import api from '@/services/api'

const applicantId = inject('applicantId')
const leaves = ref([])
const loading = ref(true)

onMounted(async () => {
  try {
    const { data } = await api.get(`get_employee_leaves.php?id=${applicantId}`) 
    // Handles both { leaves: [...] } and direct array [...] responses
    leaves.value = data?.leaves || data || [] 
  } catch (error) {
    console.error('Error fetching leaves:', error)
  } finally {
    loading.value = false
  }
})

// Helper to format dates nicely (e.g., "Oct 06, 2026")
const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

// Helper to assign Tailwind classes based on leave status
const statusClass = (status) => {
  switch (status?.toLowerCase()) {
    case 'approved':
      return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
    case 'rejected':
      return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
  }
}
</script>